import { EnrollmentJointLayout } from '@egw/layouts'
import { Button, Alert, AlertType, GenericError, Tag } from '@egw/components'
import { Logger } from '@egw/logger'
import { WEALTH_ROUTES } from '@egw/constants'
import { useEffect, type FC } from 'react'
import { useForm } from 'react-hook-form'
import {
  useGetEnrollmentJointDocumentVerificationCompleteData,
  useSubmitEnrollmentJointDocumentVerificationCompleteData,
} from '@egw/api'
import { useNavigate } from 'react-router'
import { useTranslation } from '@egw/i18n'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { UserIcon } from '@heroicons/react/16/solid'
import { EnrollmentPageLoading } from '../../../components/page-loading'
import { PageHeader } from '../../../components'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'

const log = new Logger('Enrollment - Joint - DocumentVerificationComplete')

interface FormData {
  switchSession?: boolean
}

export const DocumentVerificationCompletePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  useEffect(() => {
    // sendEvent(/* TODO: ADD EVENT FOR LANDING ON PAGE */)
  }, [])

  const { data, isLoading, error } = useGetEnrollmentJointDocumentVerificationCompleteData()
  const {
    mutate,
    isLoading: isSubmitting,
    error: submitError,
  } = useSubmitEnrollmentJointDocumentVerificationCompleteData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint document verification complete data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint document verification complete form', submitError)
    }
  }, [submitError])

  const profileName = data?.activePerson.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      switchSession: false,
    },
  })

  const onSubmit = handleSubmit(async (): Promise<void> => {
    if (isValid) {
      try {
        await mutate({
          activePerson: {
            switchSession: true,
          },
        })

        // Make another call after we switch to next person
        const response2 = await mutate({})

        // If path exists and is different
        if (response2?.activePerson?.path && newPathIsDifferent(location.pathname, response2.activePerson.path)) {
          navigate(response2.activePerson.path)
        } else {
          // If no path from BE, redirect to error
          if (!response2?.activePerson?.path) {
            log.error('No path returned from backend, redirecting to error page')
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting document verification complete form', e)
      }
    }
  })
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentJointLayout title={t('joint.document_verification_complete.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="document_verification_complete-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <CheckCircleIcon className="h-[4rem] w-[4rem] stroke-textSuccess stroke-[.5px] mb-2" />
          <PageHeader
            title={t('joint.document_verification_complete.page_title')}
            textClassName="text-text-header-success"
          />

          <div className="w-full flex-1 flex flex-col justify-start">
            <p className="typo-primary-base text-text-body">{t('joint.document_verification_complete.description')}</p>

            <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
              <div className="flex-1 md:hidden" />

              {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

              <Button
                type="submit"
                className="w-full md:w-min mt-xl"
                isLoading={isSubmitting}
                testId="primaryAction_button"
              >
                {t('common.continue')}
              </Button>
            </form>
          </div>
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
